import { itineraryRefusalSchema, memoryConflictSchema, memoryInvalidSchema, requestConflictSchema, staffQuoteConflictSchema, staffTeamConflictSchema, tripRefusalSchema, } from "@lafayette/schemas";
import { ApiHttpError, ApiNetworkError, ApiResponseError, } from "./api-failure";
import { createJsonTransport, } from "./json-transport";
/**
 * The API answered with an error. `code` is the envelope's code (`conflict` for 409, `unprocessable` for 422,
 * `forbidden` for 403, `not_found` for 404, `service_unavailable` for 503, ...). On a 409 `conflict` is the
 * memory refusal (`code`, `currentVersion`, and for staff `currentStatus`): a UI shows "This changed on another
 * device" and re-reads. On a 422 `invalidField` names the field the domain refused, never its value.
 */
export class MemoryApiError extends ApiHttpError {
    conflict;
    invalidField;
    /** CHT-736: the `tripRefusal` of a trip command's `409` or `422` (a code and a field name), or `null`. */
    tripRefusal;
    /** CHT-750: the `requestConflict` of a staff request command's `409` (a code, the current version and the current state), or `null`. */
    requestConflict;
    /**
     * CHT-757: the `quoteConflict` of a staff quote command's `409` (a rule code: `payment_request.settlement_possible` means reconcile
     * first, `payment_quote.open_payment_exists` means refund first, `payment_quote.version_conflict` means reload), or `null`.
     */
    quoteConflict;
    /**
     * CHT-742: the `itineraryRefusal` of an itinerary command's `409` or `422` (a rule code, the field that failed and, for a clock
     * gap, the first valid local time after it), or `null`.
     */
    itineraryRefusal;
    /**
     * CHT-768: the `teamConflict` of a staff team or coverage command's `409` (a rule code: `staff.stale_view`, `assignment.stale_view` and
     * `availability.stale_view` mean the screen showed old data, so re-read), or `null`.
     */
    teamConflict;
    constructor(status, code, message, requestId, extra = {}) {
        super(status, code, message, requestId, extra.retryAfter ?? null);
        this.name = "MemoryApiError";
        this.conflict = extra.conflict ?? null;
        this.invalidField = extra.invalidField ?? null;
        this.tripRefusal = extra.tripRefusal ?? null;
        this.requestConflict = extra.requestConflict ?? null;
        this.quoteConflict = extra.quoteConflict ?? null;
        this.itineraryRefusal = extra.itineraryRefusal ?? null;
        this.teamConflict = extra.teamConflict ?? null;
    }
    /** The `code` of the refusal this answer carried, whatever its area. */
    get conflictCode() {
        return (this.conflict?.code ??
            this.requestConflict?.code ??
            this.quoteConflict?.code ??
            this.teamConflict?.code ??
            this.itineraryRefusal?.code ??
            this.tripRefusal?.code ??
            null);
    }
    get detail() {
        return {
            memoryConflict: this.conflict,
            invalidField: this.invalidField,
            tripRefusal: this.tripRefusal,
            requestConflict: this.requestConflict,
            quoteConflict: this.quoteConflict,
            itineraryRefusal: this.itineraryRefusal,
            teamConflict: this.teamConflict,
            handoffConflict: null,
            payment: null,
            importRefusal: null,
        };
    }
    /**
     * `true` for a stale write: the fact, the request, the quote, a team or coverage view changed since the client read it. The itinerary and
     * the trip version conflicts are not in this list (they never were `isVersionConflict`): their screens word the code themselves.
     */
    get staleView() {
        return (this.conflict?.code === "memory.version_conflict" ||
            this.requestConflict?.code === "request.version_conflict" ||
            this.quoteConflict?.code === "payment_quote.version_conflict" ||
            this.quoteConflict?.code === "request.version_conflict" ||
            this.teamConflict?.code === "staff.stale_view" ||
            this.teamConflict?.code === "assignment.stale_view" ||
            this.teamConflict?.code === "availability.stale_view");
    }
}
/** The request never got an answer (no network, DNS, a closed connection) after every retry. */
export class MemoryApiNetworkError extends ApiNetworkError {
    constructor(options) {
        super("the memory API could not be reached", options);
        this.name = "MemoryApiNetworkError";
    }
}
/** The API answered 2xx with a body that does not match the contract. */
export class MemoryApiResponseError extends ApiResponseError {
    constructor() {
        super("the memory API answered with an unexpected body");
        this.name = "MemoryApiResponseError";
    }
}
/** The memory refusal keys of an error body, when it has them. Anything else is ignored. */
function refusalExtras(json) {
    if (typeof json !== "object" || json === null) {
        return {
            conflict: null,
            invalidField: null,
            tripRefusal: null,
            requestConflict: null,
            quoteConflict: null,
            itineraryRefusal: null,
            teamConflict: null,
        };
    }
    const body = json;
    const conflict = memoryConflictSchema.safeParse(body.memoryConflict);
    const invalid = memoryInvalidSchema.safeParse(body.memoryInvalid);
    const trip = tripRefusalSchema.safeParse(body.tripRefusal);
    const request = requestConflictSchema.safeParse(body.requestConflict);
    const quote = staffQuoteConflictSchema.safeParse(body.quoteConflict);
    const itinerary = itineraryRefusalSchema.safeParse(body.itineraryRefusal);
    const team = staffTeamConflictSchema.safeParse(body.teamConflict);
    return {
        conflict: conflict.success ? conflict.data : null,
        invalidField: invalid.success ? invalid.data.field : null,
        tripRefusal: trip.success ? trip.data : null,
        requestConflict: request.success ? request.data : null,
        quoteConflict: quote.success ? quote.data : null,
        itineraryRefusal: itinerary.success ? itinerary.data : null,
        teamConflict: team.success ? team.data : null,
    };
}
/** The error factories of the memory family. The copilot client reads its refusal answer with them. */
export const MEMORY_ERRORS = {
    refusedMessage: "the memory API refused the request",
    network: (cause) => new MemoryApiNetworkError({ cause }),
    response: () => new MemoryApiResponseError(),
    http: ({ status, code, message, requestId, retryAfter, body }) => new MemoryApiError(status, code, message, requestId, { ...refusalExtras(body), retryAfter }),
};
export function createMemoryTransport(options) {
    return createJsonTransport(options, MEMORY_ERRORS);
}
